import { MagpieConnection } from 'magpie-lib/connection';
import { useCallback, useMemo, useRef } from 'react';
import { getDeferred, getPortalId, waitForBoolean } from '../../common/utils';
import { navComponentCommsTopicDef } from '../../common/topics';
import { ncRemoteOperations } from '../../common/messages';
export function useNavigationComponent({
  componentDef
}) {
  const name = componentDef.getName();
  const platformName = componentDef.getPlatformName();
  try {
    MagpieConnection.get();
  } catch (e) {
    MagpieConnection.create({
      portalId: getPortalId()
    });
  }
  const waitForMagpieConnection = useRef(waitForBoolean(() => !!MagpieConnection.get().getClientId()));
  const show = useCallback(async () => {
    await waitForMagpieConnection.current;
    const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);
    topic.publish(ncRemoteOperations.setIsOpen.create({
      name,
      value: true,
      platformName
    }));
  }, [name, platformName]);
  const hide = useCallback(async () => {
    await waitForMagpieConnection.current;
    const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);
    topic.publish(ncRemoteOperations.setIsOpen.create({
      name,
      value: false,
      platformName
    }));
  }, [name, platformName]);
  const getState = useCallback(async () => {
    await waitForMagpieConnection.current;
    const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);
    const message = ncRemoteOperations.getState.create({
      name,
      platformName
    });
    const {
      promise,
      resolve
    } = getDeferred();
    topic.publish(message, {
      onReply: ({
        payload: componentState
      }) => {
        resolve(componentState);
      }
    });
    return promise;
  }, [name, platformName]);
  const setIsLoaded = useCallback(async () => {
    await waitForMagpieConnection.current;
    const {
      resolve,
      promise
    } = getDeferred();
    const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);
    topic.publish(ncRemoteOperations.setIsLoaded.create({
      name,
      value: true,
      platformName
    }), {
      onReply: () => resolve()
    });
    return promise;
  }, [name, platformName]);
  const setProps = useCallback(async props => {
    await waitForMagpieConnection.current;
    const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);
    topic.publish(ncRemoteOperations.setProps.create({
      name,
      value: props,
      platformName
    }));
  }, [name, platformName]);
  const getProps = useCallback(async () => {
    await waitForMagpieConnection.current;
    const {
      resolve,
      promise
    } = getDeferred();
    const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);
    topic.publish(ncRemoteOperations.getProps.create({
      name,
      platformName
    }), {
      onReply: ({
        payload
      }) => resolve(payload)
    });
    return promise;
  }, [name, platformName]);
  const invokeFunction = useCallback(async (fnName, args) => {
    await waitForMagpieConnection.current;
    const topic = MagpieConnection.get().forTopic(navComponentCommsTopicDef);
    const {
      resolve,
      promise
    } = getDeferred();
    const message = ncRemoteOperations.invokeFunction.create({
      name,
      fnName,
      args,
      platformName
    });
    topic.publish(message, {
      onReply: () => resolve()
    });
    return promise;
  }, [name, platformName]);
  return useMemo(() => ({
    show,
    hide,
    getState,
    getProps,
    setProps,
    setIsLoaded,
    invokeFunction
  }), [show, hide, getState, getProps, setProps, setIsLoaded, invokeFunction]);
}